// Budget par exercice (M06, lot L4-B, D-140, D-151) : un budget par collectivité et par exercice,
// sections fonctionnement et investissement, lignes de recettes et de dépenses à code de nomenclature
// libre (chapitre, article, paragraphe) saisies ou importées depuis un CSV (aucune nomenclature
// officielle préchargée, à confirmer par l'administration) ; états en_preparation, propose, adopte
// (référence de délibération obligatoire), executoire (date de transmission), clos ; une décision
// modificative crée des mouvements de crédits tracés, jamais une réécriture du prévu ; états
// budgétaires (balance par ligne, journal des mandats) en CSV et XLSX.
import { z } from 'zod';
import type { MontantsLigne } from './depense';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');
const texte = (max: number) => z.string().trim().min(1).max(max);

export const SECTIONS_BUDGET = { fonctionnement: 'Fonctionnement', investissement: 'Investissement' } as const;
export type SectionBudget = keyof typeof SECTIONS_BUDGET;
export const CODES_SECTIONS_BUDGET = Object.keys(SECTIONS_BUDGET) as [SectionBudget, ...SectionBudget[]];

export const SENS_LIGNE_BUDGET = { recette: 'Recettes', depense: 'Dépenses' } as const;
export type SensLigneBudget = keyof typeof SENS_LIGNE_BUDGET;
export const CODES_SENS_LIGNE = Object.keys(SENS_LIGNE_BUDGET) as [SensLigneBudget, ...SensLigneBudget[]];

export const ETATS_BUDGET = {
  en_preparation: 'En préparation',
  propose: 'Proposé au conseil',
  adopte: 'Adopté',
  executoire: 'Exécutoire',
  clos: 'Clos',
} as const;
export type EtatBudget = keyof typeof ETATS_BUDGET;
export const CODES_ETATS_BUDGET = Object.keys(ETATS_BUDGET) as [EtatBudget, ...EtatBudget[]];

/** Actes sur le budget et état atteint. */
export const ACTIONS_BUDGET = {
  proposer: { de: ['en_preparation'], vers: 'propose', libelle: 'Proposer au conseil' },
  renvoyer: { de: ['propose'], vers: 'en_preparation', libelle: 'Renvoyer en préparation' },
  adopter: { de: ['propose'], vers: 'adopte', libelle: 'Enregistrer l\'adoption' },
  rendre_executoire: { de: ['adopte'], vers: 'executoire', libelle: 'Rendre exécutoire' },
  clore: { de: ['executoire'], vers: 'clos', libelle: 'Clore l\'exercice' },
} as const satisfies Record<string, { de: readonly EtatBudget[]; vers: EtatBudget; libelle: string }>;
export type ActionBudget = keyof typeof ACTIONS_BUDGET;
export const CODES_ACTIONS_BUDGET = Object.keys(ACTIONS_BUDGET) as [ActionBudget, ...ActionBudget[]];

/** Actions possibles depuis un état. */
export function actionsBudgetPossibles(etat: EtatBudget): ActionBudget[] {
  return CODES_ACTIONS_BUDGET.filter((a) => (ACTIONS_BUDGET[a].de as readonly EtatBudget[]).includes(etat));
}

/** Les lignes (prévu) ne se modifient qu'en préparation ; ensuite seulement par décision modificative. */
export const ETATS_BUDGET_LIGNES_MODIFIABLES: readonly EtatBudget[] = ['en_preparation'];
/** Décisions modificatives : budget adopté ou exécutoire. */
export const ETATS_BUDGET_DECISIONS: readonly EtatBudget[] = ['adopte', 'executoire'];
/** Engagements : budget exécutoire seulement. */
export const ETATS_BUDGET_EXECUTION: readonly EtatBudget[] = ['executoire'];

export const schemaCodeNomenclature = z.string().trim().regex(/^[0-9A-Za-z][0-9A-Za-z.\-/ ]{0,39}$/, 'Code de nomenclature de 1 à 40 caractères (lettres, chiffres, point, tiret, barre oblique)');

// Écritures ------------------------------------------------------------------------------------

export const schemaCreationBudget = z.strictObject({
  exercice: z.number().int().min(2000).max(2100),
  libelle: texte(200),
});
export type CreationBudget = z.infer<typeof schemaCreationBudget>;

export const schemaTransitionBudget = z
  .strictObject({
    action: z.enum(CODES_ACTIONS_BUDGET),
    deliberation_reference: z.string().trim().min(2).max(120).optional(),
    date: jour.optional(),
    motif: z.string().trim().max(1000).optional(),
  })
  .refine((v) => v.action !== 'adopter' || (Boolean(v.deliberation_reference) && Boolean(v.date)), { message: 'L\'adoption exige la référence de la délibération et sa date', path: ['deliberation_reference'] })
  .refine((v) => v.action !== 'rendre_executoire' || Boolean(v.date), { message: 'La date de transmission au représentant de l\'État est exigée', path: ['date'] })
  .refine((v) => v.action !== 'renvoyer' || (v.motif?.length ?? 0) >= 3, { message: 'Motif du renvoi exigé', path: ['motif'] });
export type TransitionBudget = z.infer<typeof schemaTransitionBudget>;

const champsLigne = {
  section: z.enum(CODES_SECTIONS_BUDGET),
  sens: z.enum(CODES_SENS_LIGNE),
  code: schemaCodeNomenclature,
  chapitre: z.string().trim().max(40).optional(),
  article: z.string().trim().max(40).optional(),
  paragraphe: z.string().trim().max(40).optional(),
  libelle: texte(300),
  prevu: z.number().int('Montant entier en francs CFA').min(0).max(1_000_000_000_000),
  service_id: uuid.optional(),
};
export const schemaCreationLigne = z.strictObject(champsLigne);
export type CreationLigneBudget = z.infer<typeof schemaCreationLigne>;

export const schemaModificationLigne = z
  .strictObject({ libelle: champsLigne.libelle, prevu: champsLigne.prevu, service_id: uuid.nullable() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Au moins un champ à modifier');

/** Décision modificative : mouvements de crédits (positifs ou négatifs) sur une ou plusieurs lignes. */
export const schemaDecisionModificative = z.strictObject({
  reference: texte(80),
  date: jour,
  deliberation_reference: z.string().trim().max(120).optional(),
  motif: z.string().trim().min(3).max(1000),
  mouvements: z
    .array(z.strictObject({ ligne_id: uuid, montant: z.number().int().refine((n) => n !== 0, 'Montant non nul').refine((n) => Math.abs(n) <= 1_000_000_000_000, 'Montant trop élevé') }))
    .min(1)
    .max(50),
});
export type DecisionModificative = z.infer<typeof schemaDecisionModificative>;

/** Import de la nomenclature : contenu CSV (séparateur point-virgule ou virgule, première ligne d'en-tête). */
export const schemaImportNomenclature = z.strictObject({ csv: z.string().min(1).max(1_000_000) });

export const ETATS_BUDGETAIRES = { balance: 'Balance par ligne', mandats: 'Journal des mandats' } as const;
export type EtatBudgetaire = keyof typeof ETATS_BUDGETAIRES;
export const schemaExportBudget = z.strictObject({
  etat: z.enum(Object.keys(ETATS_BUDGETAIRES) as [EtatBudgetaire, ...EtatBudgetaire[]]),
  format: z.enum(['csv', 'xlsx']),
  section: z.enum(CODES_SECTIONS_BUDGET).optional(),
});
export const schemaFiltreLignes = z.strictObject({ section: z.enum(CODES_SECTIONS_BUDGET).optional(), sens: z.enum(CODES_SENS_LIGNE).optional() });

// Import CSV (fonction pure, partagée par l'écran d'aperçu et le serveur) ----------------------

export const COLONNES_CSV_NOMENCLATURE = ['section', 'sens', 'code', 'chapitre', 'article', 'paragraphe', 'libelle', 'prevu'] as const;

export interface ResultatImportCsv {
  lignes: CreationLigneBudget[];
  erreurs: { ligne: number; message: string }[];
}

function decouper(ligne: string, separateur: string): string[] {
  const cellules: string[] = [];
  let courante = '';
  let guillemets = false;
  for (let i = 0; i < ligne.length; i += 1) {
    const c = ligne[i] as string;
    if (c === '"') {
      if (guillemets && ligne[i + 1] === '"') {
        courante += '"';
        i += 1;
      } else guillemets = !guillemets;
    } else if (c === separateur && !guillemets) {
      cellules.push(courante.trim());
      courante = '';
    } else courante += c;
  }
  cellules.push(courante.trim());
  return cellules;
}

const SECTIONS_TEXTE: Record<string, SectionBudget> = { fonctionnement: 'fonctionnement', f: 'fonctionnement', investissement: 'investissement', i: 'investissement' };
const SENS_TEXTE: Record<string, SensLigneBudget> = { recette: 'recette', recettes: 'recette', r: 'recette', depense: 'depense', depenses: 'depense', d: 'depense' };
const sansAccent = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();

/**
 * Lit un CSV de nomenclature : en-tête obligatoire (colonnes section, sens, code, libelle, prevu ;
 * chapitre, article, paragraphe facultatives, dans n'importe quel ordre), séparateur ; ou , ,
 * montants entiers (espaces de milliers acceptés). Un code en double est signalé.
 */
export function analyserCsvNomenclature(contenu: string): ResultatImportCsv {
  // Marque d'ordre des octets UTF-8 (0xFEFF) retirée.
  const texteBrut = contenu.charCodeAt(0) === 0xfeff ? contenu.slice(1) : contenu;
  const lignesTexte = texteBrut.split(/\r?\n/).filter((l) => l.trim().length > 0);
  const erreurs: ResultatImportCsv['erreurs'] = [];
  if (lignesTexte.length < 2) return { lignes: [], erreurs: [{ ligne: 1, message: 'Le fichier doit contenir une ligne d\'en-tête et au moins une ligne' }] };
  const premiere = lignesTexte[0] as string;
  const separateur = (premiere.match(/;/g)?.length ?? 0) >= (premiere.match(/,/g)?.length ?? 0) ? ';' : ',';
  const entete = decouper(premiere, separateur).map(sansAccent);
  const position = (nom: string) => entete.indexOf(nom);
  for (const obligatoire of ['section', 'sens', 'code', 'libelle', 'prevu']) {
    if (position(obligatoire) < 0) erreurs.push({ ligne: 1, message: `Colonne « ${obligatoire} » absente de l'en-tête` });
  }
  if (erreurs.length > 0) return { lignes: [], erreurs };
  const lignes: CreationLigneBudget[] = [];
  const vus = new Set<string>();
  lignesTexte.slice(1).forEach((texteLigne, i) => {
    const numero = i + 2;
    const c = decouper(texteLigne, separateur);
    const valeur = (nom: string) => {
      const p = position(nom);
      return p >= 0 ? (c[p] ?? '').trim() : '';
    };
    const section = SECTIONS_TEXTE[sansAccent(valeur('section'))];
    const sens = SENS_TEXTE[sansAccent(valeur('sens'))];
    const prevuTexte = valeur('prevu').replace(/\s/g, ''); // \s couvre aussi les espaces insécables des milliers.
    const prevu = /^\d+$/.test(prevuTexte) ? Number(prevuTexte) : Number.NaN;
    const candidat = {
      section,
      sens,
      code: valeur('code'),
      chapitre: valeur('chapitre') || undefined,
      article: valeur('article') || undefined,
      paragraphe: valeur('paragraphe') || undefined,
      libelle: valeur('libelle'),
      prevu,
    };
    const r = schemaCreationLigne.safeParse(candidat);
    if (!r.success) {
      const champ = String(r.error.issues[0]?.path[0] ?? '');
      erreurs.push({ ligne: numero, message: `${champ ? `${champ} : ` : ''}${r.error.issues[0]?.message ?? 'ligne invalide'}` });
      return;
    }
    const cle = `${r.data.section}/${r.data.sens}/${r.data.code.toUpperCase()}`;
    if (vus.has(cle)) {
      erreurs.push({ ligne: numero, message: `Code ${r.data.code} en double pour cette section et ce sens` });
      return;
    }
    vus.add(cle);
    lignes.push(r.data);
  });
  return { lignes, erreurs };
}

// Réponses -------------------------------------------------------------------------------------

export interface Budget {
  id: string;
  exercice: number;
  libelle: string;
  etat: EtatBudget;
  deliberation_reference: string | null;
  adopte_le: string | null;
  transmis_le: string | null;
  clos_le: string | null;
  fictif: boolean;
  actions: ActionBudget[];
  totaux: Record<SectionBudget, Record<SensLigneBudget, MontantsLigne>>;
  transitions: { de: EtatBudget | null; vers: EtatBudget; le: string; par: string | null; reference: string | null; motif: string | null }[];
}

export interface LigneBudgetaire extends MontantsLigne {
  id: string;
  budget_id: string;
  section: SectionBudget;
  sens: SensLigneBudget;
  code: string;
  chapitre: string | null;
  article: string | null;
  paragraphe: string | null;
  libelle: string;
  service: { id: string; nom: string | null } | null;
}

export interface MouvementCredit {
  id: string;
  ligne: { id: string; code: string; libelle: string; section: SectionBudget; sens: SensLigneBudget };
  decision_reference: string;
  decision_le: string;
  deliberation_reference: string | null;
  montant: number;
  motif: string;
  cree_le: string;
  cree_par: string | null;
}
